import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, fmtBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth, can } from "@/lib/auth";
import { Navigate } from "react-router-dom";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Contracts() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    selected_service_ids: [],
    service_details: {},          // { [service_id]: "descrição livre" }
    services_extras: "",
    extras_detail: "",            // descrição opcional para os 'outros serviços'
    obra: "",
    include_editoriais: true,
    include_bienal: false,
    valor: 0,
    forma_pagamento: "",
    quantidade: "10",
    contract_date: todayISO(),
    bienal_data: "",
    hora_inicio: "",
    hora_fim: "",
    bienal_valor: 0,
    bienal_forma: "",
  });

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data || []));
    api.get("/services").then(r => setServices((r.data || []).filter(s => s.ativo !== false)));
  }, []);

  // Auto-detect Bienal/Editoração based on selected services
  const autoDetectSections = (selectedIds) => {
    const selected = services.filter(s => selectedIds.includes(s.id));
    const names = selected.map(s => (s.nome || "").toLowerCase());
    const hasBienal = names.some(n => n.includes("bienal"));
    const hasEditorial = selected.length > 0 && !names.every(n => n.includes("bienal"));
    return { include_bienal: hasBienal, include_editoriais: hasEditorial };
  };

  const toggleService = (id) => {
    setForm(f => {
      const exists = f.selected_service_ids.includes(id);
      const next = exists ? f.selected_service_ids.filter(x => x !== id) : [...f.selected_service_ids, id];
      const auto = autoDetectSections(next);
      return {
        ...f,
        selected_service_ids: next,
        include_bienal: auto.include_bienal || f.include_bienal,
        include_editoriais: auto.include_editoriais || f.include_editoriais,
      };
    });
  };

  const servicesText = useMemo(() => {
    const list = services.filter(s => form.selected_service_ids.includes(s.id)).map(s => s.nome);
    if (form.services_extras.trim()) {
      list.push(...form.services_extras.split(",").map(s => s.trim()).filter(Boolean));
    }
    return list;
  }, [services, form.selected_service_ids, form.services_extras]);

  const servicesDetail = useMemo(() => {
    const arr = services
      .filter(s => form.selected_service_ids.includes(s.id))
      .map(s => ({ nome: s.nome, descricao: (form.service_details[s.id] || "").trim() }));
    if (form.services_extras.trim() && form.extras_detail.trim()) {
      form.services_extras.split(",").map(s => s.trim()).filter(Boolean).forEach(n => {
        arr.push({ nome: n, descricao: form.extras_detail.trim() });
      });
    }
    return arr;
  }, [services, form.selected_service_ids, form.service_details, form.services_extras, form.extras_detail]);

  const canDo = can(user, "generate_contracts");
  if (user && !canDo) return <Navigate to="/" replace />;

  const generate = async () => {
    if (!form.client_id) return toast.error("Selecione o cliente");
    if (!form.include_editoriais && !form.include_bienal) return toast.error("Selecione ao menos uma seção (Editoração ou Bienal)");
    if (form.include_editoriais && !form.valor) return toast.error("Informe o valor dos serviços editoriais");
    if (form.include_bienal && !form.bienal_valor) return toast.error("Informe o valor da Bienal");
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API}/contracts/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({
          client_id: form.client_id,
          include_editoriais: form.include_editoriais,
          include_bienal: form.include_bienal,
          services_text: servicesText,
          services_detail: servicesDetail,
          obra: form.obra,
          valor: Number(form.valor) || 0,
          forma_pagamento: form.forma_pagamento,
          quantidade: form.quantidade,
          contract_date: form.contract_date,
          bienal_data: form.bienal_data,
          hora_inicio: form.hora_inicio,
          hora_fim: form.hora_fim,
          bienal_valor: Number(form.bienal_valor) || 0,
          bienal_forma: form.bienal_forma,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `contrato_${form.contract_date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contrato gerado");
    } catch (e) {
      toast.error(e.message || "Erro ao gerar contrato");
    } finally {
      setLoading(false);
    }
  };

  const client = clients.find(c => c.id === form.client_id);

  return (
    <Layout title="Gerar Contrato" subtitle="Emissão de contrato em PDF a partir do template oficial">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-sm p-6 lg:col-span-2 space-y-5" data-testid="contract-form-card">
          <div>
            <Label className="text-xs font-semibold">Cliente (AUTOR)</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="contract-client-select" className="mt-1"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
              <SelectContent>{clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.nome} {c.cpf ? `— CPF ${c.cpf}` : ""}</SelectItem>))}</SelectContent>
            </Select>
            {client && (
              <p className="text-xs text-muted-foreground mt-2">
                {client.endereco} {client.numero}, {client.bairro}, {client.cidade}/{client.estado} — CEP {client.cep || "—"}
              </p>
            )}
          </div>

          <Separator />

          <div>
            <Label className="text-xs font-semibold">Nome da(s) obra(s) literária(s)</Label>
            <Input
              data-testid="contract-obra"
              placeholder='Ex.: "O Jardim dos Espelhos" ou "Volume I — Sombras" e "Volume II — Luz"'
              className="mt-1"
              value={form.obra}
              onChange={(e) => setForm({ ...form, obra: e.target.value })}
            />
            <p className="text-[10px] text-muted-foreground mt-1">Será inserido na cláusula 1 (DO OBJETO) após a lista de serviços.</p>
          </div>

          <Separator />

          <div>
            <Label className="text-xs font-semibold">Serviços contratados</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-2 rounded-sm border border-border bg-secondary/20">
              {services.map(s => (
                <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    data-testid={`contract-service-${s.id}`}
                    checked={form.selected_service_ids.includes(s.id)}
                    onCheckedChange={() => toggleService(s.id)}
                  />
                  <span>{s.nome}</span>
                </label>
              ))}
              {services.length === 0 && <p className="text-xs text-muted-foreground col-span-2">Nenhum serviço cadastrado.</p>}
            </div>

            {form.selected_service_ids.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Detalhes/Variações por serviço</p>
                {services.filter(s => form.selected_service_ids.includes(s.id)).map(s => (
                  <div key={s.id}>
                    <Label className="text-xs">{s.nome}</Label>
                    <Textarea
                      data-testid={`contract-service-detail-${s.id}`}
                      rows={2}
                      placeholder={`Detalhes/variação para "${s.nome}" (ex.: formato, tipo de capa, quantidade de páginas, escopo específico…)`}
                      value={form.service_details[s.id] || ""}
                      onChange={(e) => setForm(f => ({ ...f, service_details: { ...f.service_details, [s.id]: e.target.value } }))}
                      className="mt-1 text-xs"
                    />
                  </div>
                ))}
              </div>
            )}

            <Input
              data-testid="contract-services-extras"
              placeholder="Outros serviços (separados por vírgula)"
              className="mt-3"
              value={form.services_extras}
              onChange={(e) => setForm({ ...form, services_extras: e.target.value })}
            />
            {form.services_extras.trim() && (
              <Textarea
                data-testid="contract-extras-detail"
                rows={2}
                placeholder="Detalhes/variações dos 'outros serviços' acima"
                className="mt-2 text-xs"
                value={form.extras_detail}
                onChange={(e) => setForm({ ...form, extras_detail: e.target.value })}
              />
            )}
            <p className="text-[10px] text-muted-foreground mt-1">Os nomes vão para a cláusula 1 (DO OBJETO). As descrições aparecem como subitem detalhando cada serviço.</p>
          </div>

          <Separator />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-sm border border-border hover:bg-secondary/40">
              <Checkbox
                data-testid="contract-include-editoriais"
                checked={form.include_editoriais}
                onCheckedChange={(v) => setForm({ ...form, include_editoriais: !!v })}
              />
              <div>
                <div className="text-sm font-semibold">Incluir Seção I — Serviços Editoriais</div>
                <div className="text-[11px] text-muted-foreground">Cláusulas de royalties, impressão, vendas em marketplaces, etc.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-sm border border-border hover:bg-secondary/40">
              <Checkbox
                data-testid="contract-include-bienal"
                checked={form.include_bienal}
                onCheckedChange={(v) => setForm({ ...form, include_bienal: !!v })}
              />
              <div>
                <div className="text-sm font-semibold">Incluir Seção II — Bienal SP 2026</div>
                <div className="text-[11px] text-muted-foreground">Participação no estande, sessão de autógrafos, benefícios.</div>
              </div>
            </label>
          </div>

          {form.include_editoriais && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-primary">Dados — Serviços Editoriais</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Valor total (R$)</Label>
                    <Input data-testid="contract-valor" type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="mt-1" />
                    <p className="text-[10px] text-muted-foreground mt-1">{fmtBRL(Number(form.valor) || 0)}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Quantidade de exemplares</Label>
                    <Input data-testid="contract-quantidade" value={form.quantidade} onChange={(e) => setForm({ ...form, quantidade: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Data do contrato</Label>
                    <Input data-testid="contract-date" type="date" value={form.contract_date} onChange={(e) => setForm({ ...form, contract_date: e.target.value })} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Forma e prazo de pagamento</Label>
                  <Textarea
                    data-testid="contract-forma-pagamento"
                    rows={3}
                    value={form.forma_pagamento}
                    onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}
                    placeholder="Ex.: Entrada de R$ 1.000,00 via PIX e 3 parcelas de R$ 833,33 no cartão de crédito."
                    className="mt-1"
                  />
                </div>
              </div>
            </>
          )}

          {form.include_bienal && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-primary">Dados — 28ª Bienal do Livro de São Paulo</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Data do evento</Label>
                    <Input data-testid="contract-bienal-data" type="date" value={form.bienal_data} onChange={(e) => setForm({ ...form, bienal_data: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Sessão autógrafos — início</Label>
                    <Input data-testid="contract-hora-inicio" type="time" value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Sessão autógrafos — fim</Label>
                    <Input data-testid="contract-hora-fim" type="time" value={form.hora_fim} onChange={(e) => setForm({ ...form, hora_fim: e.target.value })} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Valor da Bienal (R$)</Label>
                    <Input data-testid="contract-bienal-valor" type="number" step="0.01" value={form.bienal_valor} onChange={(e) => setForm({ ...form, bienal_valor: e.target.value })} className="mt-1" />
                    <p className="text-[10px] text-muted-foreground mt-1">{fmtBRL(Number(form.bienal_valor) || 0)}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Forma de pagamento da Bienal</Label>
                    <Input data-testid="contract-bienal-forma" value={form.bienal_forma} onChange={(e) => setForm({ ...form, bienal_forma: e.target.value })} placeholder="Ex.: PIX à vista" className="mt-1" />
                  </div>
                </div>
              </div>
            </>
          )}

          <Separator />
          <div className="flex justify-end">
            <Button data-testid="contract-generate-btn" onClick={generate} disabled={loading} size="lg">
              <FileDown className="w-4 h-4 mr-2" />
              {loading ? "Gerando…" : "Gerar e Baixar PDF"}
            </Button>
          </div>
        </Card>

        <Card className="rounded-sm p-6 space-y-4 h-fit">
          <div className="flex items-center gap-2 text-sm font-semibold"><FileText className="w-4 h-4 text-primary" /> Pré-visualização</div>
          <div className="text-xs space-y-2">
            <div><span className="text-muted-foreground">Cliente:</span> <span className="font-medium">{client?.nome || "—"}</span></div>
            <div><span className="text-muted-foreground">Obra(s):</span> <span className="font-medium">{form.obra || "—"}</span></div>
            <div><span className="text-muted-foreground">Seções incluídas:</span> <span className="font-medium">
              {[form.include_editoriais && "I — Editoração", form.include_bienal && "II — Bienal", "III — Disposições Gerais"].filter(Boolean).join(", ")}
            </span></div>
            <div><span className="text-muted-foreground">Serviços:</span> <span className="font-medium">{servicesText.join(", ") || "—"}</span></div>
            {form.include_editoriais && (
              <>
                <div><span className="text-muted-foreground">Valor (editoração):</span> <span className="font-medium">{fmtBRL(Number(form.valor) || 0)}</span></div>
                <div><span className="text-muted-foreground">Exemplares:</span> <span className="font-medium">{form.quantidade || "—"}</span></div>
              </>
            )}
            {form.include_bienal && (
              <>
                <div><span className="text-muted-foreground">Bienal:</span> <span className="font-medium">{form.bienal_data || "—"} • {form.hora_inicio || "—"}–{form.hora_fim || "—"}</span></div>
                <div><span className="text-muted-foreground">Valor (bienal):</span> <span className="font-medium">{fmtBRL(Number(form.bienal_valor) || 0)}</span></div>
              </>
            )}
            <div><span className="text-muted-foreground">Data do contrato:</span> <span className="font-medium">{form.contract_date}</span></div>
          </div>
          <Separator />
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            O contrato é gerado dinamicamente a partir do template oficial. As seções não selecionadas são removidas e as cláusulas renumeradas automaticamente. Valor por extenso é gerado em pt-BR.
          </p>
        </Card>
      </div>
    </Layout>
  );
}
